import { Group, Paper, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { TicketZeitraum, Vorgangstyp } from "@msc/shared";

import { useKpiTicketLeads, type TicketEingrenzung } from "../../../hooks/useKpiTickets.js";
import { toLocalDateString } from "../../../lib/date.js";
import { TicketAuswahlModal, type TicketAuswahl } from "./TicketAuswahlModal.js";
import { TicketBlockKopf } from "./TicketBlockKopf.js";

/** Eigener Backlog (Spec 2.1c): offen heute und Netto-Zuwachs — dieselben Zahlen wie die Spalten der Lead-Übersicht. */
export function BacklogKarte({ segmentId, leadId, eingrenzung }: { segmentId: string; leadId: string; eingrenzung: TicketEingrenzung }) {
  const { t } = useTranslation();
  const [vorgangstyp, setVorgangstyp] = useState<Vorgangstyp | null>(null);
  const [zeitraum, setZeitraum] = useState<TicketZeitraum | null>(null);
  const [auswahl, setAuswahl] = useState<TicketAuswahl | null>(null);
  const { data, isLoading, isError } = useKpiTicketLeads(segmentId, zeitraum, vorgangstyp, { ...eingrenzung, leadId });
  const zeile = data?.leads.find((l) => l.employeeId === leadId) ?? null;
  const morgen = new Date();
  morgen.setDate(morgen.getDate() + 1);
  const abfrageBasis = { vorgangstyp, ...eingrenzung, leadId };
  return (
    <Paper p="md" withBorder>
      <TicketBlockKopf titel={t("pages.kpi.tickets.backlog.titel")} spiegel={data?.spiegel} vorgangstyp={vorgangstyp} onVorgangstyp={setVorgangstyp} zeitraum={zeitraum} onZeitraum={setZeitraum} zeitraumVorgabe="letzte_12_monate" />
      {isLoading ? <Skeleton height={80} /> : isError || !data ? <Text size="sm" c="red">{t("pages.kpi.tickets.fehler")}</Text> : !zeile ? (
        <Text size="sm" c="dimmed">{t(eingrenzung.beauftragungId ? "pages.kpi.tickets.backlog.ohneVerknuepfungBeauftragung" : "pages.kpi.tickets.backlog.ohneVerknuepfung")}</Text>
      ) : (
        <Group gap="xl">
          <UnstyledButton
            aria-label={t("pages.kpi.tickets.backlog.knopfOffen", { anzahl: zeile.offen })}
            onClick={() => setAuswahl({ titel: t("pages.kpi.tickets.offenHeute"), abschnitte: [{ titel: t("pages.kpi.tickets.offenHeute"), abfrage: { quelle: "offen", von: data.zeitraum.von, bis: toLocalDateString(morgen), ...abfrageBasis } }] })}
          >
            <Text size="xl" fw={700}>{zeile.offen}</Text>
            <Text size="xs" c="dimmed">{t("pages.kpi.tickets.backlog.offen")}</Text>
          </UnstyledButton>
          <UnstyledButton
            aria-label={t("pages.kpi.tickets.backlog.knopfNetto", { netto: zeile.netto })}
            onClick={() => setAuswahl({ titel: t("pages.kpi.tickets.backlog.titel"), abschnitte: [
              { titel: t("pages.kpi.tickets.erstellt"), abfrage: { quelle: "erstellt", ...data.zeitraum, ...abfrageBasis } },
              { titel: t("pages.kpi.tickets.geloest"), abfrage: { quelle: "geloest", ...data.zeitraum, ...abfrageBasis } },
            ] })}
          >
            <Text size="xl" fw={700} c={zeile.netto > 0 ? "red" : zeile.netto < 0 ? "green" : undefined}>{zeile.netto > 0 ? `+${zeile.netto}` : zeile.netto}</Text>
            <Text size="xs" c="dimmed">{t("pages.kpi.tickets.backlog.netto", { erstellt: zeile.erstellt, geloest: zeile.geloest })}</Text>
          </UnstyledButton>
        </Group>
      )}
      <TicketAuswahlModal segmentId={segmentId} auswahl={auswahl} jiraBaseUrl={data?.jiraBaseUrl ?? null} onClose={() => setAuswahl(null)} />
    </Paper>
  );
}
